// Dependencies
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import BundleCard from './BundleCard';
import MessageCard from '../../UI/MessageCard';
import { useInfiniteScroll } from './useInfiniteScroll';
// Material UI Components
import { makeStyles } from '@material-ui/core/styles';
import { Grid, CircularProgress, Fab, Typography, List, ListItem, ListItemText, Paper } from '@material-ui/core';
import { AddOutlined } from '@material-ui/icons';
import { Link as RouterLink, useLocation, withRouter } from 'react-router-dom';

import { connect } from 'react-redux';
import { setFilter, getBundles, getMoreBundles } from '../../../actions';

import { useTranslation } from 'react-i18next';
import SearchToolbar from '../../SearchToolbar';
import CSVExport from '../../UI/CSVExport';

// Tour
import { useTour } from '@reactour/tour';
import { tourSteps } from '../../TourSteps';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 15,
      paddingRight: 15,
      marginTop: 20
    },
    [theme.breakpoints.up('xs')]: {
      marginTop: 20,
      paddingLeft: 15,
      paddingRight: 15
    },
    [theme.breakpoints.up(500)]: {
      marginTop: 40,
      paddingLeft: 15,
      paddingRight: 15
    },
    [theme.breakpoints.up('md')]: {
      marginTop: 40,
      paddingLeft: 12,
      paddingRight: 12
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: 40,
      paddingLeft: 33,
      paddingRight: 33
    },
    [theme.breakpoints.up('xl')]: {
      marginTop: 40,
      paddingLeft: 80,
      paddingRight: 80
    }
  },
  topBtns: {
    padding: '1rem',
    textTransform: 'uppercase'
  },
  btn: {
    marginRight: '1rem'
  },
  search: {
    marginTop: '1.5rem'
  },
  spinnerStyles: {
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 8
  }
}));

const BundleList = (props) => {
  const { bundles, role, appConfig } = props;

  const [searchByDocument, setSearchByDocument] = useState(false);
  const { setIsOpen, setCurrentStep, setSteps } = useTour();
  const classes = useStyles();
  const [loading, loadingMore, noMoreData] = useInfiniteScroll(props, searchByDocument);
  const location = useLocation();
  const path = location.pathname.split('/');
  const last = path.pop();
  const { t } = useTranslation();
  const hideButtonAdd = (last === 'bundle' && (role === 'admin' || role === 'editor'));
  const justifyAlign = hideButtonAdd ? 'space-between' : 'flex-end';

  useEffect(() => {
    const tour = JSON.parse(localStorage.getItem('tour-bundle'));
    setSteps(tourSteps.createNewBundle);
    setCurrentStep(0);
    setIsOpen(tour);
  }, []);
  const renderSearchByDocument = (bundle) => {
    return (
      <Grid style={{ marginBottom: '40px' }} container spacing={1}>
        <Grid item xs={12}>
          <BundleCard key={bundle.id} bundle={bundle} enableTags={appConfig.enableTags} />
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography style={{ marginLeft: '20px' }}>{t('dashboard:DOCUMENTOS')}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Paper style={{ marginLeft: '40px', backgroundColor: '#ededed', marginRight: '40px' }}>
              { searchByDocument && <List className='List' width={'100%'}>
                {bundle.document && bundle.document.items.map((document) => (
                  <ListItem>
                    <ListItemText style={{
                      textTransform: 'uppercase'
                    }} primary={'- ' + document.name} />
                  </ListItem>
                ))}
              </List>}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    );
  };
  const renderBundles = (bundle) => {
    return <BundleCard key={bundle.id} bundle={bundle} enableTags={appConfig.enableTags} />;
  };

  return (
    <Grid container className={classes.mainContainer} direction='column'>
      <Grid container direction='column' item xs={12} md={10}>
        <Grid container justify={justifyAlign} >
          { hideButtonAdd &&
            <Grid item className={classes.topBtns}>
              <Fab
                component={RouterLink}
                to='/bundle/new'
                className={classes.btn}
                aria-label='Add'
                color='primary'
              >
                <AddOutlined className='bundle-newBundle' />
              </Fab>
              {t('bundleListView:newBundle')}
            </Grid>}
          <Grid item className={classes.topBtns}>
            <CSVExport data={bundles} type={'bundle'} />
          </Grid>
        </Grid>

        <Grid item>
          <div className={classes.search}>
            <SearchToolbar searchByDocument={searchByDocument} setSearchByDocument={setSearchByDocument} visibleFor={['/bundle']} />
          </div>
        </Grid>
      </Grid>

      {bundles && bundles.length ? (
        <>
          {loading ? (
            <CircularProgress />
          ) : (
            <Grid container direction='row' item xs={12} md={10}>
              {bundles.map((bundle) => {
                if (!searchByDocument) {
                  return renderBundles(bundle);
                } else if (bundle.document && bundle.document.items.length) {
                  return renderSearchByDocument(bundle);
                }
              })}
            </Grid>
          )
          }
          {loadingMore && (
            <div className={classes.spinnerStyles}>
              <CircularProgress />
            </div>
          )}
          {noMoreData && (
            <div className={classes.spinnerStyles}>
              {t('bundleListView:noMoreData')}
            </div>
          )}
        </>
      ) : loading ? (
        <CircularProgress />
      ) : (
        <MessageCard message={t('bundleListView:bundleDoesntExist')} />
      )}
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    filter: state.filter,
    bundles: state.bundles.bundles,
    role: state.config.role,
    appConfig: state.config
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setFilter: (data) => dispatch(setFilter(data)),
    getAllBundles: (searchByDocument) => dispatch(getBundles(searchByDocument)),
    getMoreBundles: (searchByDocument) =>
      dispatch(getMoreBundles(searchByDocument))
  };
};

BundleList.propTypes = {
  bundles: PropTypes.array.isRequired,
  location: PropTypes.object.isRequired,
  role: PropTypes.string.isRequired
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(BundleList)
);
